/**
 * Pinia portal-scope store (Client Portal v2 header store-scope switcher,
 * ADR 0055).
 *
 * Holds the GLOBAL store scope for the Client Portal: which store (if any) the
 * Overview / Orders / Approvals surfaces are currently scoped to. "All stores"
 * (``selectedStoreId === null``) is the aggregate default for a Client Admin;
 * a Store Manager is LOCKED to their single store and cannot change the scope.
 *
 * The selection persists across in-portal navigation for the lifetime of the
 * session (the store is a singleton), so a Client Admin who picks a store on
 * Overview still sees that store when they move to Orders. There is no
 * localStorage mirror -- the scope is a per-session view choice, re-hydrated
 * from ``GET /api/portal/stores`` on each portal entry.
 *
 * The server is the authority on BOTH the store list and the lock: the pill
 * only ever offers stores the caller may scope to, and ``store_id`` is
 * re-validated server-side on every scoped request (a cross-client store_id
 * 404s). The frontend never widens scope on its own.
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "@/axios";

export const usePortalScopeStore = defineStore("portalScope", () => {
    // ---- state ----
    const role = ref(null); // 'client_admin' | 'store_manager' | null
    const locked = ref(false); // true => Store Manager, fixed chip, no dropdown
    const stores = ref([]); // /portal/stores rows ({ id, name, close-date facts })
    const selectedStoreId = ref(null); // null => "All stores" aggregate
    const loaded = ref(false); // has load() completed at least once
    const loadError = ref(false);

    // ---- getters ----
    // The currently-scoped store row (or null for "All stores").
    const selectedStore = computed(
        () => stores.value.find((s) => s.id === selectedStoreId.value) || null,
    );

    // The label the pill renders: the store name when scoped, else "All stores".
    const scopeLabel = computed(() =>
        selectedStore.value ? selectedStore.value.name : "All stores",
    );

    // Whether a non-aggregate scope is active (drives the "active" indicator +
    // the easy return-to-all-stores affordance).
    const isScoped = computed(() => selectedStoreId.value !== null);

    // The axios ``params`` object the scoped pages spread into their GETs.
    // ``{}`` when aggregate so the request is byte-identical to the pre-scope
    // call; ``{ store_id }`` when a store is selected.
    const storeIdParams = computed(() =>
        selectedStoreId.value !== null ? { store_id: selectedStoreId.value } : {},
    );

    // ---- actions ----
    /**
     * Load the caller's role + store list + lock state from the server and
     * initialize the default scope. A Store Manager is pinned to their
     * ``scoped_store_id`` (locked); a Client Admin defaults to "All stores".
     *
     * Idempotent-ish: safe to call on every portal mount. On a lock the
     * selection is always forced back to the locked store so a Store Manager
     * can never end up on an aggregate view via stale state.
     *
     * @param {Object} [opts]
     * @param {number|string} [opts.preferredStoreId]  A store the caller
     *   ASKED for (the close-reminder magic link lands on /portal?store=<id>).
     *   Honored only when it is one of the stores the server just returned and
     *   the caller is not locked: a request can narrow the view to an allowed
     *   store, never widen it (see ``requestStore``).
     * @param {Function} [opts.onForbidden]  Called when the server returns
     *   403 -- i.e. the caller is authenticated but lacks a client scope
     *   (NULL client_id: SuperAdmin, Distributor staff, un-wired shopper).
     *   The caller (PortalLayout) should redirect to the appropriate home
     *   surface for the account's role. If omitted, the 403 is treated the
     *   same as any other error (loadError=true, no redirect).
     */
    async function load({ onForbidden, preferredStoreId } = {}) {
        loadError.value = false;
        try {
            const resp = await axios.get("/api/portal/stores");
            const data = resp?.data || {};
            role.value = data.role ?? null;
            locked.value = Boolean(data.locked);
            stores.value = Array.isArray(data.stores) ? data.stores : [];
            if (locked.value) {
                // Store Manager: pin to the server-declared store; never
                // allow an aggregate view for a locked caller.
                selectedStoreId.value = data.scoped_store_id ?? null;
            } else if (allowedStoreId(preferredStoreId) !== null) {
                // A requested store (magic-link landing) the caller may see.
                selectedStoreId.value = allowedStoreId(preferredStoreId);
            } else if (
                selectedStoreId.value !== null &&
                !stores.value.some((s) => s.id === selectedStoreId.value)
            ) {
                // Client Admin whose previously-selected store is no longer in
                // the list (removed / re-scoped) -> fall back to All stores.
                selectedStoreId.value = null;
            }
            loaded.value = true;
        } catch (e) {
            loadError.value = true;
            loaded.value = true;
            // 403 means the caller is authenticated but has no client scope
            // (NULL client_id). This is the authoritative signal that the
            // user does not belong on the portal surface. Invoke the
            // onForbidden callback so PortalLayout can redirect them to
            // their correct home rather than rendering a broken shell.
            if (e?.response?.status === 403 && typeof onForbidden === "function") {
                onForbidden();
            }
        }
    }

    /**
     * Set the active store scope. A ``null`` id selects the "All stores"
     * aggregate. A locked caller (Store Manager) cannot change scope -- the
     * call is a no-op so the fixed chip stays authoritative.
     */
    function setStore(storeId) {
        if (locked.value) return;
        selectedStoreId.value = storeId ?? null;
    }

    /**
     * The id of an allowed store matching ``raw`` (a number or an all-digit
     * string, e.g. a route query value), or null. "Allowed" means it is in
     * the server's list for this caller -- the list is the authority.
     */
    function allowedStoreId(raw) {
        const text = raw === null || raw === undefined ? "" : String(raw).trim();
        if (!/^\d+$/.test(text)) return null;
        const id = Number(text);
        return stores.value.some((s) => s.id === id) ? id : null;
    }

    /**
     * Select a REQUESTED store if the caller may see it; otherwise a no-op.
     * Unlike ``setStore`` (a pill choice from the server's own list), the id
     * here comes from outside (a URL), so it is checked against the list.
     * A locked caller (Store Manager) is never moved.
     */
    function requestStore(raw) {
        if (locked.value) return;
        const id = allowedStoreId(raw);
        if (id !== null) selectedStoreId.value = id;
    }

    /**
     * Replace one store row with a fresher copy from the server (e.g. the
     * row the close-date write returns), so every reader of the scope sees
     * the new close date without a reload. Unknown ids are ignored.
     */
    function updateStore(row) {
        if (!row || row.id === undefined) return;
        const i = stores.value.findIndex((s) => s.id === row.id);
        if (i === -1) return;
        const next = stores.value.slice();
        next[i] = { ...next[i], ...row };
        stores.value = next;
    }

    /** Convenience: return to the "All stores" aggregate. */
    function clearScope() {
        setStore(null);
    }

    /** Reset to initial state (used on logout / portal exit). */
    function reset() {
        role.value = null;
        locked.value = false;
        stores.value = [];
        selectedStoreId.value = null;
        loaded.value = false;
        loadError.value = false;
    }

    return {
        // state
        role,
        locked,
        stores,
        selectedStoreId,
        loaded,
        loadError,
        // getters
        selectedStore,
        scopeLabel,
        isScoped,
        storeIdParams,
        // actions
        load,
        setStore,
        requestStore,
        updateStore,
        clearScope,
        reset,
    };
});
